import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

export default function CategoryManagement({ apiUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches } = useBranch();
  const confirm = useConfirm();
  const [categories, setCategories] = useState([]);
  const [branchPrinters, setBranchPrinters] = useState([]); // [{id, name, role}] ของสาขาที่กำลังดูอยู่
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sortOrder: 0,
    printerType: 'kitchen'
  });

  // หมวดหมู่แยกต่อสาขาแล้ว (ไม่ใช้ร่วมกันทั้งเครือเหมือนเดิม) — ต้องเลือกสาขาที่ชัดเจนก่อน
  // ถึงจะโหลด/แก้ไขได้ เหมือนหน้าตั้งค่าเครื่องพิมพ์ ไม่งั้นจะไม่รู้ว่ากำลังแก้ของสาขาไหน
  const blocked = isAllBranches || !effectiveBranchId;

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadCategories();
  }, [effectiveBranchId, blocked]);

  // โหลดรายชื่อเครื่องพิมพ์จริงของสาขาที่กำลังดูอยู่ — ให้เลือกเป็นชื่อเครื่องพิมพ์ตรงๆ
  // แทนพิมพ์ role เอง (category เป็นรายการรวมทุกสาขา เก็บได้แค่ role slug อยู่ดี แต่ UI
  // ให้เลือกจากชื่อจริงของสาขาที่กำลังดู แล้วแปลงเป็น role ให้อัตโนมัติ)
  useEffect(() => {
    if (isAllBranches || !effectiveBranchId) {
      setBranchPrinters([]);
      return;
    }
    loadBranchPrinters();
  }, [effectiveBranchId, isAllBranches]);

  const loadBranchPrinters = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/printer/config`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-Branch-Id': effectiveBranchId }
      });
      const result = await response.json();
      setBranchPrinters(result.data?.printers || []);
    } catch (err) {
      console.error('Failed to load branch printers:', err);
      setBranchPrinters([]);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const data = result.data || result;
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('ไม่สามารถโหลดข้อมูลหมวดหมู่ได้');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!branchPrinters.some(p => p.role === formData.printerType)) {
      setMessage({ type: 'error', text: 'กรุณาเลือกเครื่องพิมพ์ที่มีอยู่จริงของสาขานี้ก่อนบันทึก' });
      return;
    }

    const token = localStorage.getItem('adminToken');
    const url = editingId
      ? `${apiUrl}/categories/${editingId}`
      : `${apiUrl}/categories`;
    const method = editingId ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'เกิดข้อผิดพลาด');
      }

      setMessage({ type: 'success', text: editingId ? 'บันทึกการแก้ไขสำเร็จ' : 'เพิ่มหมวดหมู่สำเร็จ' });

      // Reset form
      setFormData({ name: '', description: '', sortOrder: 0, printerType: 'kitchen' });
      setEditingId(null);
      setShowForm(false);

      // Reload list
      await loadCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', sortOrder: 0, printerType: 'kitchen' });
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    const printerType = category.printerType || 'kitchen';
    setFormData({
      name: category.name,
      description: category.description || '',
      sortOrder: category.sortOrder || 0,
      printerType
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm('คุณต้องการลบหมวดหมู่นี้หรือไม่?', { title: 'ลบหมวดหมู่', confirmText: 'ลบ', danger: true })) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'ไม่สามารถลบหมวดหมู่ได้');
      }

      await loadCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleToggle = async (id) => {
    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/categories/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('ไม่สามารถเปลี่ยนสถานะได้');
      }

      await loadCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', sortOrder: 0, printerType: 'kitchen' });
    setShowForm(false);
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — หมวดหมู่แยกอิสระต่อสาขาแล้ว
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการตั้งค่าผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">กำลังโหลดข้อมูล...</div>;
  }

  return (
    <div className="category-management">
      <h2>จัดการหมวดหมู่</h2>

      {error && <div className="error-message">{error}</div>}
      <Toast message={message} onDismiss={() => setMessage(null)} />

      <button type="button" className="btn-primary" onClick={handleAddNew} style={{ marginBottom: '1.5rem' }}>
        ➕ เพิ่มหมวดหมู่ใหม่
      </button>

      <Modal open={showForm} onClose={handleCancel} title={editingId ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่ใหม่'}>
        <form onSubmit={handleSubmit} className="category-form">
          <div className="form-group">
            <label>ชื่อหมวดหมู่ *</label>
            <input
              type="text"
              className="dark-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="เช่น เนื้อสัตว์, ผัก, ลูกชิ้น"
            />
          </div>

          <div className="form-group">
            <label>คำอธิบาย</label>
            <textarea
              className="dark-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="คำอธิบายหมวดหมู่ (ถ้ามี)"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ลำดับการแสดง</label>
              <input
                type="number"
                className="dark-input"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>เครื่องพิมพ์{currentBranch ? ` (${currentBranch.name})` : ''}</label>
              {branchPrinters.length === 0 ? (
                <small className="field-hint" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                  ⚠️ สาขานี้ยังไม่มีเครื่องพิมพ์ตั้งค่าไว้ กรุณาไปตั้งค่าเครื่องพิมพ์ก่อนจึงจะบันทึกหมวดหมู่ได้
                </small>
              ) : (
                <>
                  <select
                    className="dark-input"
                    value={branchPrinters.some(p => p.role === formData.printerType) ? formData.printerType : ''}
                    onChange={(e) => setFormData({ ...formData, printerType: e.target.value })}
                  >
                    <option value="" disabled>-- เลือกเครื่องพิมพ์ --</option>
                    {branchPrinters.map(p => (
                      <option key={p.id} value={p.role}>{p.name}</option>
                    ))}
                  </select>
                  {!branchPrinters.some(p => p.role === formData.printerType) && (
                    <small className="field-hint" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      ⚠️ ค่าเดิม ("{formData.printerType}") ไม่ตรงกับเครื่องพิมพ์เครื่องไหนของสาขานี้แล้ว กรุณาเลือกเครื่องพิมพ์ใหม่
                    </small>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editingId ? 'บันทึกการแก้ไข' : 'เพิ่มหมวดหมู่'}
            </button>
            <button type="button" onClick={handleCancel} className="btn-secondary">
              ยกเลิก
            </button>
          </div>
        </form>
      </Modal>

      <div className="categories-list">
        <h3>รายการหมวดหมู่ทั้งหมด ({categories.length})</h3>
        <div style={{ overflowX: 'auto' }}>
        <table className="data-table table-nowrap">
          <thead>
            <tr>
              <th>ลำดับ</th>
              <th>ชื่อหมวดหมู่</th>
              <th>คำอธิบาย</th>
              <th>เครื่องพิมพ์</th>
              <th>สถานะ</th>
              <th>การจัดการ</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>ไม่มีข้อมูลหมวดหมู่</td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id}>
                  <td>{category.sortOrder || '-'}</td>
                  <td><strong>{category.name}</strong></td>
                  <td>{category.description || '-'}</td>
                  <td>
                    <span className="printer-badge">
                      🖨️ {branchPrinters.find(p => p.role === (category.printerType || 'kitchen'))?.name || category.printerType || 'kitchen'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${category.isActive !== false ? 'active' : 'inactive'}`}>
                      {category.isActive !== false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(category)}
                        className="btn-edit"
                        title="แก้ไข"
                      >
                        แก้ไข
                      </button>
                      <button
                        onClick={() => handleToggle(category.id)}
                        className="btn-toggle"
                        title="เปิด/ปิดใช้งาน"
                      >
                        {category.isActive !== false ? 'ปิด' : 'เปิด'}
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="btn-delete"
                        title="ลบ"
                      >
                        ลบ
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <style jsx>{`
        .category-management {
          padding: 20px;
        }

        h2 {
          margin-bottom: 30px;
          color: var(--text);
        }

        h3 {
          margin-bottom: 15px;
          color: var(--text);
        }

        .error-message {
          background: #2a1517;
          border: 1px solid #8a2f33;
          color: var(--danger);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .success-message {
          background: rgba(63, 191, 127, 0.12);
          border: 1px solid var(--ok);
          color: var(--ok);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .category-form-container {
          background: var(--surface-2);
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 40px;
        }

        .category-form {
          max-width: 600px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: var(--text);
        }

        .form-group input[type="text"],
        .form-group input[type="number"],
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border-strong);
          border-radius: 4px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-group input[type="checkbox"] {
          margin-right: 8px;
        }

        .btn-link-role {
          background: none;
          border: none;
          color: var(--info);
          font-size: 12px;
          padding: 6px 0 0;
          cursor: pointer;
          text-decoration: underline;
        }

        .field-hint {
          display: block;
          margin-top: 6px;
          color: var(--muted);
          font-size: 12px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .btn-primary,
        .btn-secondary,
        .btn-edit,
        .btn-toggle,
        .btn-delete {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .btn-primary {
          background: var(--accent);
          color: #1a1206;
        }

        .btn-primary:hover {
          filter: brightness(1.08);
        }

        .btn-secondary {
          background: var(--surface-2);
          color: var(--text-2);
          border: 1px solid var(--border-strong);
        }

        .btn-secondary:hover {
          background: var(--surface);
        }

        .categories-list {
          margin-top: 40px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--surface);
        }

        .data-table th,
        .data-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid var(--border);
          color: var(--text-2);
        }

        .data-table th {
          background: var(--surface-2);
          color: var(--muted);
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .data-table tr:hover {
          background: var(--surface-2);
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.active {
          background: rgba(63, 191, 127, 0.15);
          color: var(--ok);
        }

        .status-badge.inactive {
          background: rgba(239, 91, 91, 0.15);
          color: var(--danger);
        }

        .printer-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          background: rgba(110, 168, 254, 0.15);
          color: var(--info);
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-edit {
          background: var(--surface-2);
          color: var(--ok);
          border: 1px solid var(--border-strong);
          padding: 6px 12px;
          font-size: 13px;
        }

        .btn-edit:hover {
          background: var(--surface);
        }

        .btn-toggle {
          background: var(--surface-2);
          color: var(--warn);
          border: 1px solid var(--border-strong);
          padding: 6px 12px;
          font-size: 13px;
        }

        .btn-toggle:hover {
          background: var(--surface);
        }

        .btn-delete {
          background: var(--surface-2);
          color: var(--danger);
          border: 1px solid var(--border-strong);
          padding: 6px 12px;
          font-size: 13px;
        }

        .btn-delete:hover {
          background: var(--surface);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
